/**
 * CheckinForm orchestrator - 4-step daily wellness check-in (Feature 019).
 *
 * Manages a single state object across all 4 steps, handles navigation,
 * progress display, and submission to the backend API.
 */

import React, { useState } from 'react';
import { CheckInInputs, SleepQuality } from '../../types';
import SleepStep, { SleepStepData } from './SleepStep';
import FeelingStep, { FeelingStepData } from './FeelingStep';
import HormonalStep, { HormonalStepData } from './HormonalStep';
import ActivityStep, { ActivityStepData } from './ActivityStep';

const SLEEP_QUALITY_TO_INT: Record<SleepQuality, number> = {
  poor: 1, fair: 2, good: 3, great: 4, excellent: 5,
};

interface CheckinFormProps {
  onSubmit: (inputs: CheckInInputs) => Promise<void>;
  isLoading: boolean;
  initialValues?: CheckInInputs;
  submitButtonText?: string;
  onCancel?: () => void;
}

const STEP_TITLES = [
  'Sleep & Recovery',
  'Feeling & Mood',
  'Hormonal Status',
  "Yesterday's Activity",
];

const CheckinForm: React.FC<CheckinFormProps> = ({
  onSubmit,
  isLoading,
  initialValues,
  submitButtonText = 'Submit Check-In',
  onCancel
}) => {
  const [currentStep, setCurrentStep] = useState(1);
  const totalSteps = 4;

  // Unified form state
  const [sleepData, setSleepData] = useState<SleepStepData>({
    sleep_quality: (initialValues?.sleep_quality as any) || 'good',
    sleep_from_time: initialValues?.sleep_from_time,
    sleep_to_time: initialValues?.sleep_to_time,
    night_waking: initialValues?.night_waking,
    wake_feeling: initialValues?.wake_feeling,
    resting_heart_rate: initialValues?.resting_heart_rate,
  });

  const [feelingData, setFeelingData] = useState<FeelingStepData>({
    energy_level: initialValues?.energy_level || 'medium',
    mood: initialValues?.mood as any,
    body_symptoms: initialValues?.body_symptoms && initialValues.body_symptoms.length > 0
      ? initialValues.body_symptoms
      : ['none'],
    custom_symptom: initialValues?.custom_symptom,
  });

  const [hormonalData, setHormonalData] = useState<HormonalStepData>({
    waist_feeling: initialValues?.waist_feeling,
    cycle_day: initialValues?.cycle_day,
    cycle_status: initialValues?.cycle_status,
    craving_level: initialValues?.craving_level,
  });

  const [activityData, setActivityData] = useState<ActivityStepData>({
    activity_type: initialValues?.activity_type,
    activity_intensity: initialValues?.activity_intensity,
    activity_duration: initialValues?.activity_duration,
    steps: initialValues?.steps,
  });

  const canProceed = () => {
    if (currentStep === 1) return !!sleepData.sleep_quality;
    if (currentStep === 2) return !!feelingData.energy_level;
    return true; // Steps 3 and 4 are entirely optional
  };

  const nextStep = () => {
    if (currentStep < totalSteps && canProceed()) {
      setCurrentStep(currentStep + 1);
    }
  };

  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Build submission payload from all steps
    const inputs: CheckInInputs = {
      // Step 1: Sleep (convert string enum to int 1-5 for backend)
      sleep_quality: SLEEP_QUALITY_TO_INT[sleepData.sleep_quality] || 3,
      ...(sleepData.sleep_from_time && { sleep_from_time: sleepData.sleep_from_time }),
      ...(sleepData.sleep_to_time && { sleep_to_time: sleepData.sleep_to_time }),
      ...(sleepData.night_waking && { night_waking: sleepData.night_waking }),
      ...(sleepData.wake_feeling && { wake_feeling: sleepData.wake_feeling }),
      ...(sleepData.resting_heart_rate && { resting_heart_rate: sleepData.resting_heart_rate }),

      // Step 2: Feeling
      energy_level: feelingData.energy_level,
      ...(feelingData.mood && { mood: feelingData.mood }),
      body_symptoms: feelingData.body_symptoms,
      ...(feelingData.custom_symptom && { custom_symptom: feelingData.custom_symptom }),

      // Step 3: Hormonal
      ...(hormonalData.waist_feeling && { waist_feeling: hormonalData.waist_feeling }),
      ...(hormonalData.cycle_day != null && { cycle_day: hormonalData.cycle_day }),
      ...(hormonalData.cycle_status && { cycle_status: hormonalData.cycle_status }),
      ...(hormonalData.craving_level && { craving_level: hormonalData.craving_level }),

      // Step 4: Activity
      ...(activityData.activity_type && { activity_type: activityData.activity_type }),
      ...(activityData.activity_intensity && { activity_intensity: activityData.activity_intensity }),
      ...(activityData.activity_duration != null && { activity_duration: activityData.activity_duration }),
      ...(activityData.steps != null && { steps: activityData.steps }),
    };

    await onSubmit(inputs);
  };

  return (
    <form onSubmit={handleSubmit} className="card shadow-elegant max-w-3xl mx-auto">
      {/* Progress Indicator */}
      <div className="mb-8">
        <div className="flex justify-between items-center mb-3">
          <span className="text-sm font-medium text-neutral-600">
            Step {currentStep} of {totalSteps}: {STEP_TITLES[currentStep - 1]}
          </span>
          <span className="text-sm font-medium text-primary-600">
            {Math.round((currentStep / totalSteps) * 100)}%
          </span>
        </div>
        <div className="bg-primary-100 rounded-full h-3 overflow-hidden">
          <div
            className="bg-gradient-to-r from-primary-500 to-primary-600 h-full transition-all duration-500 ease-out rounded-full"
            style={{ width: `${(currentStep / totalSteps) * 100}%` }}
          ></div>
        </div>
        {/* Step dots */}
        <div className="flex justify-between mt-2">
          {STEP_TITLES.map((_, idx) => (
            <div
              key={idx}
              className={`w-2.5 h-2.5 rounded-full transition-colors ${
                idx + 1 <= currentStep ? 'bg-primary-500' : 'bg-primary-200'
              }`}
            />
          ))}
        </div>
      </div>

      {/* Step Content */}
      {currentStep === 1 && (
        <SleepStep data={sleepData} onChange={setSleepData} />
      )}
      {currentStep === 2 && (
        <FeelingStep data={feelingData} onChange={setFeelingData} />
      )}
      {currentStep === 3 && (
        <HormonalStep data={hormonalData} onChange={setHormonalData} />
      )}
      {currentStep === 4 && (
        <ActivityStep data={activityData} onChange={setActivityData} />
      )}

      {/* Navigation Buttons */}
      <div className="flex justify-between items-center mt-10 pt-6 border-t border-primary-200">
        <button
          type="button"
          onClick={prevStep}
          disabled={currentStep === 1}
          className="btn btn-outline px-6 py-3 disabled:opacity-30 disabled:cursor-not-allowed"
        >
          Back
        </button>

        {currentStep < totalSteps ? (
          <button
            type="button"
            onClick={nextStep}
            disabled={!canProceed()}
            className="btn btn-primary px-8 py-3 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Next
          </button>
        ) : (
          <div className="flex items-center space-x-4">
            {onCancel && (
              <button
                type="button"
                onClick={onCancel}
                disabled={isLoading}
                className="btn bg-neutral-100 text-neutral-700 hover:bg-neutral-200 px-8 py-3 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
            )}
            <button
              type="submit"
              disabled={isLoading || !canProceed()}
              className="btn btn-accent px-8 py-3 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? (
                <span className="flex items-center space-x-2">
                  <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  <span>Saving...</span>
                </span>
              ) : (
                <span>{submitButtonText}</span>
              )}
            </button>
          </div>
        )}
      </div>
    </form>
  );
};

export default CheckinForm;
